import {ParameterDefinition, ParameterValues} from '@/model/api/parameters';
import {Parameters} from '@/model/offline/storage/ParameterStorage';
import {fromPairs} from 'lodash-es';

interface StoredEstimates {
    [estimateId: string]: ParameterValues;
}

type ParameterValue = ParameterDefinition['value'];

export class StoredParameters implements Parameters {
    constructor(
        private readonly estimateId: string,
        private readonly storageKey: string,
    ) {
    }

    public add(parameterName: string, value: ParameterValue): void {
        const estimates = this.getFromStorage();

        if (!(this.estimateId in estimates)) {
            estimates[this.estimateId] = {};
        }

        estimates[this.estimateId][parameterName] = value;

        this.saveToStorage(estimates);
    }

    public getAll(): { [parameterName: string]: ParameterValue } {
        return this.getFromStorage()[this.estimateId] || {};
    }

    public remove(...parameterNames: string[]): void {
        const estimates = this.getFromStorage();

        estimates[this.estimateId] = fromPairs(
            Object.entries(estimates[this.estimateId] || {})
                .filter(([parameterName]) => !parameterNames.includes(parameterName)),
        );

        this.saveToStorage(estimates);
    }

    public removeAll(): void {
        const estimates = this.getFromStorage();

        delete estimates[this.estimateId];

        this.saveToStorage(estimates);
    }

    public saveToStorage(estimates: StoredEstimates): void {
        localStorage.setItem(this.storageKey, JSON.stringify(estimates));
    }

    private getFromStorage(): StoredEstimates {
        const cacheValue = localStorage.getItem(this.storageKey);

        if (cacheValue === null) {
            return {};
        }

        return JSON.parse(cacheValue);
    }
}
